import React, { useEffect, useState } from 'react';
import { Users, Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { OPERATORS, LINES } from '../mock/data';
import { PageHeader, StatusBadge, Modal, Field } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { formatShift } from '../utils/shifts';
import api from '../api/client';

const META = { ON_DUTY: { label: 'On Duty', cls: 's-completed' }, OFF_DUTY: { label: 'Off Duty', cls: 's-idle' } };
const EMPTY = { operator: OPERATORS[0], shift: '', line: 'Line A', machines: '', status: 'ON_DUTY' };

export default function OperatorAssignments() {
  const [items, setItems] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const shiftOptions = shifts.map(formatShift);

  const load = () => api.listAssignments().then(setItems).catch(() => {}).finally(() => setLoading(false));
  const loadShifts = () => api.getShifts().then(setShifts).catch(() => {});
  useEffect(() => { load(); loadShifts(); }, []);
  useRealtime(() => { load(); loadShifts(); });

  const openCreate = () => { setForm({ ...EMPTY, shift: shiftOptions[0] || '' }); setModal({ mode: 'create' }); };
  const openEdit = (a) => { setForm({ ...a, machines: (a.machines || []).join(', ') }); setModal({ mode: 'edit' }); };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, machines: form.machines.split(',').map((s) => s.trim()).filter(Boolean) };
    try {
      if (modal.mode === 'create') {
        const created = await api.createAssignment(payload);
        setItems((p) => [...p, created]);
        toast.success(`Assignment added`);
      } else {
        const upd = await api.updateAssignment(form.id, payload);
        setItems((p) => p.map((x) => (x.id === upd.id ? upd : x)));
        toast.success(`Assignment updated`);
      }
      setModal(null);
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    setItems((p) => p.filter((x) => x.id !== a.id));
    try { await api.deleteAssignment(a.id); toast.success('Assignment removed'); }
    catch (e) { toast.error('Delete failed'); load(); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Users} title="Operator Assignments" subtitle={`${items.length} operators · shift roster`}
        right={<button className="kmt-btn-gold flex items-center gap-2" onClick={openCreate}><Plus size={16} />Add Assignment</button>} />
      {loading ? (
        <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
      ) : (
        <div className="p-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} className="kmt-panel p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center font-display font-bold" style={{ background: 'var(--kmt-panel-2)', color: 'var(--kmt-gold)', border: '1px solid var(--kmt-border)' }}>{a.operator[0]}</div>
                  <div>
                    <div className="font-display font-medium">{a.operator}</div>
                    <div className="kmt-label" style={{ fontSize: 9 }}>{a.line}</div>
                  </div>
                </div>
                <StatusBadge meta={META[a.status] || META.OFF_DUTY} />
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span style={{ color: 'var(--kmt-muted)' }}>Shift</span><span>{a.shift}</span></div>
                <div className="flex justify-between"><span style={{ color: 'var(--kmt-muted)' }}>Machines</span><span className="font-mono-k" style={{ color: 'var(--kmt-gold)' }}>{(a.machines || []).join(', ') || '—'}</span></div>
              </div>
              <div className="flex gap-2 mt-4 pt-3" style={{ borderTop: '1px solid var(--kmt-border-soft)' }}>
                <button className="kmt-btn-ghost text-xs flex items-center gap-1.5 flex-1 justify-center" onClick={() => openEdit(a)}><Pencil size={13} />Edit</button>
                <button className="kmt-btn-ghost text-xs flex items-center gap-1.5" onClick={() => remove(a)} style={{ color: 'var(--kmt-red)' }}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'Add Assignment' : 'Edit Assignment'}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="kmt-btn-gold" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Operator">
            <input className="kmt-input" value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} list="opers" />
            <datalist id="opers">{OPERATORS.map((o) => <option key={o} value={o} />)}</datalist>
          </Field>
          <Field label="Line">
            <select className="kmt-input" value={form.line} onChange={(e) => setForm({ ...form, line: e.target.value })}>
              {LINES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Shift">
            <select className="kmt-input" value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} data-testid="assignment-shift-select">
              {!shiftOptions.includes(form.shift) && form.shift && <option value={form.shift}>{form.shift}</option>}
              {shiftOptions.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className="kmt-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="ON_DUTY">On Duty</option>
              <option value="OFF_DUTY">Off Duty</option>
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Machines (comma separated)">
              <input className="kmt-input" value={form.machines} placeholder="M02, M12" onChange={(e) => setForm({ ...form, machines: e.target.value })} />
            </Field>
          </div>
        </div>
      </Modal>
    </div>
  );
}
